import { makeAutoObservable, runInAction } from 'mobx';
import { EmfitAPI } from '../services/BaseAPI';
import { STATUS_API } from 'src/config';

/**
 * Notification delivery readiness per caregiver.
 *
 * GET /v1/group-user/push-token-statuses is admin-only (AdminRoleGuard).
 * last_seen_at is the newest mobile-channel audit event for that caregiver,
 * or null if their app has never checked in.
 */
export interface PushTokenStatus {
  id: number;
  group_id: number;
  login: string;
  name: string;
  has_fcm: boolean;
  has_apn: boolean;
  last_seen_at: string | null;
}

class PushTokenStoreClass {
  statuses: PushTokenStatus[] = [];
  loading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** A caregiver can receive notifications if either token type is registered. */
  get readyCount(): number {
    return this.statuses.filter((s) => s.has_fcm || s.has_apn).length;
  }

  get notReadyCount(): number {
    return this.statuses.length - this.readyCount;
  }

  async fetchStatuses(): Promise<void> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const result = await EmfitAPI.get<PushTokenStatus[]>(
        `${STATUS_API}/v1/group-user/push-token-statuses`,
        { noCache: true }
      );
      runInAction(() => {
        this.statuses = Array.isArray(result) ? result : [];
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to load notification status';
        this.loading = false;
      });
      console.error('Failed to fetch push token statuses:', error);
    }
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this.statuses = [];
    this.loading = false;
    this.error = null;
  }
}

export const PushTokenStore = new PushTokenStoreClass();
